import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import {
  Box,
  Button,
  Chip,
  IconButton,
  Link as MuiLink,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router';
import { listTenants } from '../../api/endpoints';
import { queryKeys } from '../../api/queryKeys';
import type { TenantSummary } from '../../api/types';
import { EmptyBlock, ErrorBlock, LoadingBlock } from '../../components/QueryStateOverlay';
import { AdminSectionTabs } from './AdminSectionTabs';
import { TenantDialog } from './TenantDialog';

export function AdminPage() {
  const { t } = useTranslation();
  const [editing, setEditing] = useState<TenantSummary | 'new' | null>(null);

  const query = useQuery({
    queryKey: queryKeys.admin.tenants(),
    queryFn: ({ signal }) => listTenants(signal),
  });

  const tenants = query.data ?? [];

  return (
    <Stack spacing={2}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box>
          <Typography variant="h5">{t('admin.title')}</Typography>
          <Typography variant="body2" color="text.secondary">
            {t('admin.tenants.description')}
          </Typography>
        </Box>
        <Button startIcon={<AddIcon />} variant="contained" onClick={() => setEditing('new')}>
          {t('admin.tenants.register')}
        </Button>
      </Box>

      <AdminSectionTabs />

      {query.isPending && <LoadingBlock />}
      {query.isError && <ErrorBlock error={query.error} onRetry={() => void query.refetch()} />}

      {query.isSuccess &&
        (tenants.length === 0 ? (
          <EmptyBlock message={t('admin.tenants.empty')} />
        ) : (
          <Paper variant="outlined">
            <TableContainer sx={{ overflowX: 'auto' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>{t('columns.name')}</TableCell>
                    <TableCell>{t('admin.tenants.code')}</TableCell>
                    <TableCell align="right">{t('admin.tabs.accounts')}</TableCell>
                    <TableCell>{t('columns.state')}</TableCell>
                    <TableCell align="right">{t('columns.actions')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {tenants.map((tenant) => (
                    <TableRow key={tenant.tenantGid} hover>
                      <TableCell>
                        <MuiLink component={RouterLink} to={`/admin/tenant/${tenant.tenantGid}`}>
                          {tenant.name}
                        </MuiLink>
                      </TableCell>
                      <TableCell sx={{ fontFamily: 'monospace' }}>{tenant.code}</TableCell>
                      <TableCell align="right">{tenant.accountCount}</TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          variant="outlined"
                          color={tenant.enabled ? 'success' : 'default'}
                          label={tenant.enabled ? t('states.enabled') : t('states.disabled')}
                        />
                        {tenant.testMode && (
                          <Chip
                            size="small"
                            color="warning"
                            label={t('admin.tenantDetail.testModeChip')}
                            sx={{ ml: 1 }}
                          />
                        )}
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title={t('actions.edit')}>
                          <IconButton size="small" onClick={() => setEditing(tenant)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        ))}

      {editing !== null && (
        <TenantDialog tenant={editing === 'new' ? null : editing} onClose={() => setEditing(null)} />
      )}
    </Stack>
  );
}
